// client/src/pages/marketing/PostDetailPage.jsx
import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Navbar from '../../components/marketing/Navbar';
import Footer from '../../components/marketing/Footer';
import { getPostBySlug } from '../../data/nieuwsbrief';

const PostDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = getPostBySlug(slug);

  // If post not found, show 404
  if (!post) {
    return (
      <div className="min-h-screen bg-cream-100 font-['Lexend_Deca']">
        <Navbar />
        <div className="container mx-auto max-w-4xl py-20 px-5 mt-[70px]">
          <div className="text-center py-20">
            <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h1 className="text-2xl text-gray-800 mb-2">Bericht niet gevonden</h1>
            <p className="text-gray-600 mb-6">Dit nieuwsbericht bestaat niet of is niet meer beschikbaar.</p>
            <Link 
              to="/nieuwsbrief"
              className="inline-block px-6 py-3 bg-purple-500 text-white font-semibold rounded-md hover:bg-purple-600 transition-colors"
            >
              Terug naar overzicht
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Format content for display
  const formatContent = (content) => {
    if (!content) return null;

    const lines = content.split('\n');
    const result = [];
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmed = line.trim();
      
      if (!trimmed) {
        continue; // Skip empty lines, they become paragraph breaks
      }
      
      // Handle headers starting with **
      if (trimmed.startsWith('**') && trimmed.endsWith('**')) {
        const headerText = trimmed.replace(/\*\*/g, '');
        result.push(
          <h3 key={i} className="font-['Lexend_Deca'] text-xl font-medium text-charcoal-800 mt-6 mb-3">
            {headerText}
          </h3>
        );
        continue;
      }
      
      // Handle bold text within paragraphs
      if (trimmed.includes('**')) {
        const parts = trimmed.split('**');
        result.push(
          <p key={i} className="text-charcoal-700 mb-4 leading-relaxed">
            {parts.map((part, partIndex) => 
              partIndex % 2 === 1 ? (
                <strong key={partIndex} className="font-semibold text-charcoal-900">
                  {part}
                </strong>
              ) : (
                part
              )
            )}
          </p>
        );
        continue;
      }
      
      // Handle bullet points
      if (trimmed.startsWith('-')) {
        result.push(
          <li key={i} className="text-charcoal-700 mb-2 ml-4">
            {trimmed.replace(/^\-\s*/, '')}
          </li>
        );
        continue;
      }
      
      // Handle quotes
      if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
        result.push(
          <p key={i} className="text-charcoal-700 mb-4 leading-relaxed italic border-l-4 border-purple-500 pl-4 py-2">
            {trimmed}
          </p>
        );
        continue;
      }
      
      // Regular paragraphs
      result.push(
        <p key={i} className="text-charcoal-700 mb-4 leading-relaxed">
          {trimmed}
        </p>
      );
    }
    
    return result;
  };

  return (
    <div className="min-h-screen bg-cream-100 text-charcoal-800 font-['Lexend_Deca'] text-base leading-relaxed">
      <Navbar />
      
      <div className="container mx-auto max-w-4xl py-20 px-5 mt-[70px]">
        {/* Back button */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center text-purple-600 hover:text-purple-800 font-medium transition-colors"
          >
            <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            Terug naar overzicht
          </button>
        </div>

        {/* Featured image */}
        {post.imageUrl && (
          <div className="mb-8 rounded-lg overflow-hidden">
            <img 
              src={post.imageUrl} 
              alt={post.title}
              className="w-full h-96 object-cover"
            />
          </div>
        )}

        {/* Post header */}
        <header className="mb-8">
          {/* Category badge */}
          {post.category && (
            <div className="mb-3">
              <span className="inline-block px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                {post.category}
              </span>
            </div>
          )}

          {/* Title */}
          <h1 className="font-['Lexend_Deca'] text-4xl font-light text-gray-800 mb-4">
            {post.title}
          </h1>

          {/* Metadata */}
          <div className="flex items-center text-gray-500 text-sm">
            <time dateTime={post.date}>
              {new Date(post.date).toLocaleDateString('nl-NL', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </time>
            
            {post.author && (
              <>
                <span className="mx-2">•</span>
                <span>{post.author}</span>
              </>
            )}
          </div>
        </header>

        {/* Post content */}
        <article className="bg-white rounded-lg border border-gray-200 p-8 md:p-12">
          <div className="prose prose-lg max-w-none">
            {formatContent(post.content)}
          </div>
        </article>

        {/* Back button at bottom */}
        <div className="mt-8 text-center">
          <Link
            to="/nieuwsbrief"
            className="inline-block px-6 py-3 bg-purple-500 text-white font-semibold rounded-md hover:bg-purple-600 transition-colors"
          >
            Terug naar alle berichten
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PostDetailPage;